// Phase 34c.3 — slide-over panel showing one distribution's full breakdown.
//
// Opened when an LP clicks a row in /investor's distribution history table.
// Re-uses TierSegmentBar for the visual + a clean numeric table below.

import { Link } from "react-router-dom";
import { ExternalLink, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import TierSegmentBar, { TIER_TINT } from "@/components/syndication/TierSegmentBar";

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium tabular-nums text-slate-900">{value}</span>
    </div>
  );
}

export default function DistributionDetailSlideover({ row, fmt, onDownloadPdf, onDownloadXlsx }) {
  const tiers = row.my_tiers || { pref: 0, return_of_capital: 0, catch_up: 0, promote: 0 };
  const total = row.my_total_received || 0;

  return (
    <div className="mt-6 space-y-6">
      {/* Hero total */}
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 px-5 py-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
          Your distribution
        </div>
        <div className="mt-1 text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
          {fmt.money(total)}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {new Date(row.computed_at).toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          })}
        </div>
      </div>

      {/* Meta */}
      <div className="space-y-2.5 rounded-2xl border border-slate-200 bg-white px-5 py-4">
        <MetaRow label="Partnership" value={row.partnership_name || "—"} />
        <MetaRow label="Deal" value={row.deal_address || "—"} />
        <MetaRow label="Source" value={(row.source || "—").toUpperCase()} />
      </div>

      {/* Tier breakdown — proportional bar + tinted table */}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Per-tier breakdown
        </div>
        <TierSegmentBar
          tiers={tiers}
          total={total}
          testid="investor-distribution-detail-tier-bar"
          compact
        />
        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {[
                { key: "pref",              label: "Preferred return" },
                { key: "return_of_capital", label: "Return of capital" },
                { key: "catch_up",          label: "GP catch-up" },
                { key: "promote",           label: "Promote" },
              ].map((t) => {
                const amount = Number(tiers[t.key] || 0);
                const palette = TIER_TINT[t.key] || {};
                if (amount <= 0.005) return null;
                return (
                  <tr key={t.key} className={`${palette.tint || ""}`}>
                    <td className="px-4 py-2.5 text-slate-700">{t.label}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${palette.text || "text-slate-900"}`}>
                      {fmt.money(amount)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 font-semibold">
                <td className="px-4 py-2.5 text-slate-900">Total received</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">
                  {fmt.money(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={onDownloadPdf} className="gap-2" data-testid="investor-distribution-detail-pdf-btn">
          <FileText className="h-4 w-4" />
          Download statement (PDF)
        </Button>
        <Button onClick={onDownloadXlsx} variant="outline" className="gap-2" data-testid="investor-distribution-detail-xlsx-btn">
          <FileSpreadsheet className="h-4 w-4" />
          Download Excel
        </Button>
      </div>

      <Link
        to={`/syndication/partnerships/${row.partnership_id}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
        data-testid="investor-distribution-detail-open-partnership"
      >
        Open partnership <ExternalLink className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
